/**
 * Récupère la config statique du workspace (servie potentiellement par CDN).
 *
 * URL : {apiBaseUrl}/sdk/workspaces/{publicKey}/config.json
 *
 * En cas d'échec réseau, on retourne null (fail silent). Le loader ne fera
 * simplement rien sur cette page — aucune erreur n'est propagée au site client.
 */

import type { WorkspaceConfig } from "../shared/types";

const FETCH_TIMEOUT_MS = 5000;

export async function fetchConfig(
  apiBaseUrl: string,
  publicKey: string,
): Promise<WorkspaceConfig | null> {
  const url = `${apiBaseUrl}/sdk/workspaces/${encodeURIComponent(publicKey)}/config.json`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      signal: controller.signal,
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as unknown;

    if (!isWorkspaceConfig(data)) {
      return null;
    }

    return data;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Déduit l'URL de l'API par défaut depuis l'URL du SDK.
 *
 *  - https://b.sdk.spread.family  → https://b.api.spread.family
 *  - https://sdk.halmya.fr        → https://api.halmya.fr
 *  - https://cdn.example.com/sdk  → null (pas un sous-domaine `sdk.*`)
 *
 * Une fois la config chargée, si `config.apiDomain` est défini (custom tracking
 * domain configuré côté workspace), le core l'utilisera pour les calls suivants.
 */
export function deriveApiBaseUrl(sdkBaseUrl: string): string | null {
  try {
    const url = new URL(sdkBaseUrl);
    const hostname = url.hostname;

    if (!/(^|\.)sdk(\.|$)/.test(hostname)) {
      return null;
    }

    const apiHostname = hostname.replace(/(^|\.)sdk(\.|$)/, "$1api$2");
    url.hostname = apiHostname;
    url.pathname = "";

    return url.origin;
  } catch {
    return null;
  }
}

function isWorkspaceConfig(value: unknown): value is WorkspaceConfig {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const v = value as Record<string, unknown>;

  return (
    typeof v.publicKey === "string" &&
    (v.apiDomain === null || typeof v.apiDomain === "string") &&
    Array.isArray(v.allowedDomains) &&
    typeof v.allowEmptyDomains === "boolean" &&
    typeof v.features === "object" &&
    v.features !== null &&
    typeof v.theme === "object" &&
    v.theme !== null &&
    typeof v.consent === "object" &&
    v.consent !== null
  );
}
